/**
 * HustleRoundupPreJoinPage — mic/cam device test using LiveKit `<PreJoin/>`.
 *
 * Route: /meet/prejoin/:roomName
 * On submit, we stash the chosen display name in sessionStorage and jump
 * to /meet/:roomName which handles the actual token mint + LiveKit connect.
 */
import { useNavigate, useParams } from "react-router-dom";
import { PreJoin } from "@livekit/components-react";
import "@livekit/components-styles";
import { useEffect, useState } from "react";
import { fetchMe } from "@/lib/api";

export default function HustleRoundupPreJoinPage() {
  const { roomName } = useParams();
  const nav = useNavigate();
  const [me, setMe]   = useState(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    (async () => {
      const u = await fetchMe();
      if (!u?.id) {
        nav(`/login?next=/meet/prejoin/${encodeURIComponent(roomName)}`);
        return;
      }
      setMe(u);
      setReady(true);
    })();
  }, [nav, roomName]);

  if (!ready) {
    return (
      <div data-testid="hustleroundup-prejoin-loading"
           className="min-h-screen bg-stone-950 flex items-center justify-center text-stone-400 text-sm">
        Loading device preview…
      </div>
    );
  }

  const onSubmit = (choices) => {
    try {
      sessionStorage.setItem(
        `hr-prejoin-${roomName}`,
        JSON.stringify({
          name: choices.username || me?.name || "Guest",
          videoEnabled: choices.videoEnabled,
          audioEnabled: choices.audioEnabled,
          videoDeviceId: choices.videoDeviceId,
          audioDeviceId: choices.audioDeviceId,
        }),
      );
    } catch { /* ignore */ }
    nav(`/meet/${encodeURIComponent(roomName)}`);
  };

  return (
    <div
      data-testid="hustleroundup-prejoin"
      className="min-h-screen bg-stone-950 text-white flex flex-col"
      data-lk-theme="default"
    >
      <div className="p-4 border-b border-stone-900 flex items-center gap-2">
        <button
          data-testid="hustleroundup-prejoin-back"
          onClick={() => nav("/meet")}
          className="text-stone-400 hover:text-white text-sm px-2 py-1"
        >
          ← Back
        </button>
        <div className="ml-auto text-xs text-stone-500">
          Room: <code className="text-emerald-400/80">{roomName}</code>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center">
        <PreJoin
          onSubmit={onSubmit}
          defaults={{ username: me?.name || me?.email?.split("@")[0] || "" }}
          data-testid="hustleroundup-prejoin-widget"
        />
      </div>
    </div>
  );
}
